/* Keep cart mutations ordered; insurance is part of a successful product add. */
(() => {
  if (window.AACCartSync) return;
  const nativeFetch = window.fetch.bind(window);
  const protectionId = 54957062488402;
  let pending = Promise.resolve();
  let revision = 0;
  const root = () => window.Shopify?.routes?.root || '/';
  const isProtection = item => Number(item.variant_id || item.id) === protectionId;
  const disabled = () => {
    try { return sessionStorage.getItem('shipping-protection-disabled') === 'true'; }
    catch { return false; }
  };
  const readCart = async () => {
    const response = await nativeFetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' }, cache: 'no-store',
    });
    if (!response.ok) throw new Error('Could not read cart');
    return response.json();
  };

  window.AACCartSync = {
    get revision() { return revision; },
    async whenIdle() {
      let current;
      do { current = pending; await current; } while (current !== pending);
    },
  };

  window.fetch = function(input, init) {
    const url = new URL(input instanceof Request ? input.url : String(input), window.location.href);
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const mutation = url.origin === window.location.origin && method === 'POST' &&
      url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/);
    if (!mutation) {
      const cartRead = url.origin === window.location.origin && method === 'GET' &&
        (/\/cart\.(js|json)$/.test(url.pathname) ||
          url.searchParams.get('sections')?.split(',').includes('cart-drawer-section') ||
          url.searchParams.get('section_id') === 'cart-drawer-section');
      if (!cartRead) return nativeFetch(input, init);
      return window.AACCartSync.whenIdle().then(() => nativeFetch(input, { ...init, cache: 'no-store' }));
    }

    revision++;
    const task = pending.then(async () => {
      const response = await nativeFetch(input, init);
      if (!response.ok) return response;
      let result;
      try { result = await response.clone().json(); } catch { return response; }
      if (result.status || result.errors) return response;

      // Never let an insurance failure turn a successful product add into an error.
      try {
        const cart = await readCart();
        const realItems = cart.items.filter(item => !isProtection(item));
        const insurance = cart.items.filter(isProtection);
        const addedItems = result.items || (result.variant_id ? [result] : []);
        const shouldAdd = mutation[1] === 'add' && addedItems.some(item => !isProtection(item)) &&
          realItems.length > 0 && insurance.length === 0 && !disabled();
        let changed = false;
        if (shouldAdd) {
          const added = await nativeFetch(`${root()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: protectionId, quantity: 1,
              properties: { _shipping_protection: 'true' } }] }),
          });
          if (!added.ok) throw new Error('Could not add shipping protection');
          changed = true;
        } else if (realItems.length === 0 && insurance.length > 0) {
          for (const item of insurance) {
            const removed = await nativeFetch(`${root()}cart/change.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: item.key, quantity: 0 }),
            });
            if (!removed.ok) throw new Error('Could not remove orphan shipping protection');
            changed = true;
          }
        }
        if (changed) {
          // change/update responses are full carts; add responses remain added items.
          if (mutation[1] !== 'add') {
            const sections = result.sections;
            result = await readCart();
            if (sections) result.sections = sections;
          }
          if (result.sections) {
            const sectionsUrl = new URL(window.location.href);
            sectionsUrl.searchParams.set('sections', Object.keys(result.sections).join(','));
            const rendered = await nativeFetch(sectionsUrl.toString(), { cache: 'no-store' });
            if (!rendered.ok) throw new Error('Could not refresh cart sections');
            result.sections = await rendered.json();
          }
          const headers = new Headers(response.headers);
          headers.delete('content-length');
          headers.delete('content-encoding');
          headers.set('content-type', 'application/json');
          return new Response(JSON.stringify(result), { status: response.status, statusText: response.statusText, headers });
        }
      } catch (error) {
        // Fall back to a fresh section render instead of replaying pre-insurance HTML.
        if (result.sections) result.sections = {};
        console.error('[Shipping Protection]', error);
        return new Response(JSON.stringify(result), {
          status: response.status, headers: { 'Content-Type': 'application/json' },
        });
      }
      return response;
    });
    pending = task.then(() => undefined, () => undefined);
    return task;
  };
})();
